import { apiFetch, classifiedFetch, type TokenStore } from './client.ts';

export type SubscriptionStatus =
  | 'pilot'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'suspended';


export interface MembershipCredential {
  /** Endorsement id (verify/reject/cert-view target); registrationId is the cert-upload/withdraw target. */
  id: string;
  registrationId: string;
  bodyCode: string;
  bodyName: string;
  registrationNumber: string;
  status: 'claimed' | 'verified' | 'rejected' | 'expired';
  /** Whether a certificate file is on record for this credential. */
  hasCertificate: boolean;
  /** Registration expiry (epoch ms); null on pre-v7.61 rows. */
  expiresAt: number | null;
  /** Why the practice rejected it - only set on the holder's own rejected rows. */
  rejectionReason: string | null;
}

export interface PracticeMembership {
  practiceId: string;
  practiceName: string;
  roleInPractice: string;
  dpaAccepted: boolean;
  /**
   * Whether the practice holds the CURRENT DPA terms, terms since superseded
   * ('stale'), or has never signed. `dpaAccepted` is true for any active
   * agreement however old, so it cannot express 'stale' - gating reads this.
   */
  dpaStatus: 'never' | 'stale' | 'current';
  /**
   * For 'stale' only: when the current terms take effect + the notice period.
   * Before it, the owner is nudged; after it, the owner (never staff) is blocked.
   */
  dpaReacceptDueAt: number | null;
  /**
   * Whether the practice holds the CURRENT Terms of Service (the Principal Agreement),
   * a version since superseded, or has never accepted any.
   *
   * There is no `tosReacceptDueAt`: ToS clause 11 promises no notice window, so 'stale'
   * blocks the owner exactly as 'never' does. See derivePolicyGate.
   */
  tosStatus: 'never' | 'stale' | 'current';
  /** ADR 0004 - the user's business-authority in this practice (owner ⇒ both true).
   * front-office = enquiries; back-office = contracts + templates + billing. */
  frontOfficeAuthority: boolean;
  backOfficeAuthority: boolean;
  /**
   * Capabilities this membership confers, resolved SERVER-SIDE. Render from this, not
   * from `roleInPractice` — a role comparison here is a copy of a server rule that is
   * free to drift from it, which is how a tutor came to be offered a screen the server
   * denies (2026-07-22). Practice-scoped: it never answers "may I do X on case Y".
   */
  capabilities: string[];
  /** Billing/issuer identity set up enough to issue a usable invoice (H1). */
  billingProfileComplete: boolean;
  /** Whether the practice has ≥1 active contract template (setup checklist). */
  contractTemplateReady: boolean;
  /** The user's professional registrations in this practice (role display). */
  credentials: MembershipCredential[];
  /** True when the user holds ≥1 verified credential in this practice. */
  isVerifiedPractitioner: boolean;
  icoRegistrationNumber: string | null;
  icoRegistrationExpiresAt: number | null;
  trialEndsAt: number | null;
  /** Cases + agreements counted toward the plan. Only non-zero once owing — see trial.ts. */
  heldSlots: number;
  /** The practice plan's status; cancelled/suspended is plan-only for every member. */
  subscriptionStatus: SubscriptionStatus | null;
  /** Cancelled/suspended, OR 14+ days owing (ToS 9.5, terms-2.4) — `subscriptionStatus` alone
   *  cannot say the ladder half, since `status` stays 'pilot'/'trialing' throughout. */
  planOnly: boolean;
  /** 7-13 days owing (ToS 9.5, terms-2.4): reads/export/plan/payments stay open, nothing else
   *  can be added. Never true when the practice is already plan-only. */
  readOnly: boolean;
  autoMasteryNoticeDue: boolean;
  leadCasesAwaiting: ReadonlyArray<{ caseId: string; clientName: string }>;
  /** When the owner finished the guided setup round; null while it is still owed. */
  setupCompletedAt: number | null;
  /** The practice has a primary contact email. */
  practiceDetailsComplete: boolean;
  /** The practice has an active postal address of its own, of either kind it may hold. */
  practiceAddressComplete: boolean;
  /** Assembled server-side (src/lib/setup-catalogue.ts). */
  setupRound: {
    steps: SetupRoundStep[];
    owed: boolean;
  };
}

export interface SetupRoundStep {
  key: string;
  scope: 'person' | 'practice' | 'practice_membership' | 'case_membership';
  label: string;
  blurb: string;
  required: boolean;
  done: boolean;
  href: string | null;
  view: { scopeId: string; role: string } | null;
}

/** GET /me while the MFA gate still holds the session — the gate chain's own
 *  inputs only, so the shell routes off it rather than rendering from it. */
export interface MeGatePending {
  mfaGate: 'pending';
  email: string;
  emailVerified: boolean;
  mfaRequired: boolean;
  mfaEnrolled: boolean;
  recommendedMfaFactor: 'totp' | 'email_otp';
  activeMfaFactor: 'totp' | 'email_otp' | null;
  mfaUpgradeRequired: boolean;
  sessionMfaVerified: boolean;
}

/** Either shape GET /me can answer with; discriminated on `mfaGate`. */
export type MePayload = MeResponse | MeGatePending;

export interface MeResponse {
  mfaGate: 'passed';
  id: string;
  email: string;
  displayName: string;
  /** Optional contact phone (V6 gap 1-A). */
  phone: string | null;
  selfCertPromptDismissedAt: number | null;
  /** Practice roles whose first-login "how this works" walkthrough (Practice Compass) this
   *  user has already dismissed. Compared against the roles their CURRENT memberships hold —
   *  a role held for the first time gets its own walkthrough once. */
  roleIntroDismissedRoles: string[];
  role: string;
  status: string;
  /** Whether this account must enrol MFA (drives the enrolment gate; keyed on the
   * stored flag, not the account role - ADR 0001 Addendum A). */
  mfaRequired: boolean;
  mfaEnrolled: boolean;
  /** Whether the user has proven inbox ownership (v7.26). ProtectedLayout shows a
   * blocking verification interstitial (before the MFA gate) while this is false. */
  emailVerified: boolean;
  emailVerificationSent: boolean;
  /**
   * Whether this user has been shown the CURRENT privacy-notice version.
   *
   * Used by PrivacyNoticeScreen to decide whether opening the page should record
   * a delivery. It is NOT a gate and there is deliberately no prompt built on
   * it: a privacy notice is provided, not agreed to, and our lawful bases are
   * contract and legitimate interests, never consent.
   */
  privacyNoticeSeen: boolean;
  privacyNoticeBannerDue: boolean;
  /** An earlier version was delivered, so the banner may say the notice changed. */
  privacyNoticeUpdatedSinceSeen: boolean;
  /** Which second factor this persona should ENROL: 'email_otp' only for client/tutor. */
  recommendedMfaFactor: 'totp' | 'email_otp';
  /** The factor the account must actually be CHALLENGED with, null when it holds
   * none. Verification surfaces branch on this - branching on the recommendation
   * would show them a form they cannot satisfy. */
  activeMfaFactor: 'totp' | 'email_otp' | null;
  /** Enforcement rests on a factor this persona has outgrown: the server answers
   * MFA_UPGRADE_REQUIRED off the MFA bootstrap routes. Read through mfaStep(). */
  mfaUpgradeDueAt: number | null;
  mfaUpgradeRequired: boolean;
  /** Whether the current session has satisfied MFA. The email-OTP gate is
   * per-login. Carried across token refresh (per session-family). */
  sessionMfaVerified: boolean;
  roleAcknowledgementDue: RoleAcknowledgementDue[];
  createdAt: number;
  updatedAt: number | null;
  /** Live subscription snapshot, or null when no subscription row exists. */
  /** Active practice memberships - reload-safe practice navigation source. */
  memberships: PracticeMembership[];
}

export interface RoleAcknowledgementDue {
  scope: 'practice' | 'case';
  practiceId: string;
  practiceName: string;
  caseId: string | null;
  role: string;
  version: string;
  text: string;
}

export async function acknowledgeRole(
  due: RoleAcknowledgementDue,
  store: TokenStore,
): Promise<void> {
  await acknowledgeRoles([due], store);
}

export async function acknowledgeRoles(
  dues: readonly RoleAcknowledgementDue[],
  store: TokenStore,
): Promise<void> {
  const post = (body: object) =>
    apiFetch<{ ok: true }>('/me/role-acknowledgements', { method: 'POST', body: JSON.stringify(body) }, store);
  for (const d of dues.filter((x) => x.scope !== 'case')) {
    await post({ version: d.version, practiceId: d.practiceId, role: d.role });
  }
  const cases = dues.filter((x) => x.scope === 'case');
  if (cases.length > 0) {
    await post({ version: cases[0]!.version, caseIds: cases.map((d) => d.caseId), role: cases[0]!.role });
  }
}

export interface MyRecordedAcknowledgement {
  version: string;
  text: string;
  acknowledgedAt: number;
}

/** The actor's own already-recorded undertaking for one scope+role (FB-0033/FB-0045). */
export async function getMyRoleAcknowledgement(
  scopeId: string,
  role: string,
  store: TokenStore,
): Promise<MyRecordedAcknowledgement> {
  return apiFetch<MyRecordedAcknowledgement>(
    `/me/role-acknowledgements?scopeId=${encodeURIComponent(scopeId)}&role=${encodeURIComponent(role)}`,
    {},
    store,
  );
}

export interface PatchMeBody {
  displayName: string;
  /** Omit to leave unchanged; '' or null to clear (V6 gap 1-A). */
  phone?: string | null;
}

export async function getMe(store: TokenStore): Promise<MePayload> {
  return apiFetch<MePayload>('/me', {}, store);
}

/** Update the authenticated user's display name. Returns refreshed profile. */
export async function patchMe(body: PatchMeBody, store: TokenStore): Promise<MeResponse> {
  return apiFetch<MeResponse>('/me', {
    method: 'PATCH',
    body: JSON.stringify(body),
  }, store);
}

// ── Sessions ──────────────────────────────────────────────────────────────────

export interface SessionSummary {
  id: string;
  /** True for the session backing the current request. */
  current: boolean;
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number;
  deviceLabel: string | null;
  ipAddress: string | null;
}

/** List active sessions for the authenticated user. GET /auth/sessions */
export async function listSessions(store: TokenStore): Promise<SessionSummary[]> {
  const res = await apiFetch<{ sessions: SessionSummary[] }>('/auth/sessions', {}, store);
  return res.sessions;
}

/** Revoke a specific session. DELETE /auth/sessions/:id */
export async function revokeSession(
  sessionId: string,
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ success: boolean }>(
    `/auth/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE' },
    store,
  );
}

/** Sign out of all other devices - revokes every session except the current one (V6 gap 1-B). */
export async function revokeOtherSessions(store: TokenStore): Promise<number> {
  const res = await apiFetch<{ success: boolean; revokedCount: number }>(
    '/auth/sessions/revoke-others',
    { method: 'POST' },
    store,
  );
  return res.revokedCount;
}

// ── Password change ───────────────────────────────────────────────────────────

export interface ChangePasswordBody {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: true;
  /** How many OTHER sessions were signed out — this one stays active. */
  otherSessionsRevoked: number;
}

/** POST /auth/password/change — the authenticated user changes their own password. */
export async function changePassword(
  body: ChangePasswordBody,
  store: TokenStore,
): Promise<ChangePasswordResponse> {
  return apiFetch<ChangePasswordResponse>('/auth/password/change', {
    method: 'POST',
    body: JSON.stringify(body),
  }, store);
}

// ── Email delivery status (v7.86) ────────────────────────────────────────────

/** Self-addressed auth emails whose delivery status a user can be shown. */
export type SelfEmailTemplate = 'mfa_email_otp' | 'email_verification' | 'account_activation';

export interface EmailDelivery {
  /** pending | sent | delivered | bounced | complained | failed (or null pre-send). */
  status: string | null;
  /** Human-facing reason when the send failed (bounce/complaint/send-failure). */
  failureReason: string | null;
  sentAt: number | null;
  templateType: string;
}

/**
 * GET /me/email-deliveries - delivery status of the latest self-addressed send of
 * `templateType` (server orders by enqueued_at; the client polls only after its
 * own send resolves, so the latest row is that send's).
 */
export async function getMyEmailDelivery(
  templateType: SelfEmailTemplate,
  store: TokenStore,
): Promise<EmailDelivery | null> {
  const res = await apiFetch<{ delivery: EmailDelivery | null }>(
    `/me/email-deliveries?templateType=${encodeURIComponent(templateType)}`,
    {},
    store,
  );
  return res.delivery;
}

/** GET /contracts/:id/signing-email-status - delivery status of the signing email. */
export async function getContractSigningEmailStatus(
  contractId: string,
  store: TokenStore,
): Promise<EmailDelivery | null> {
  const res = await apiFetch<{ delivery: EmailDelivery | null }>(
    `/contracts/${encodeURIComponent(contractId)}/signing-email-status`,
    {},
    store,
  );
  return res.delivery;
}

/** GET /invitations/:id/email-status - delivery status of the invitation email. */
export async function getInvitationEmailStatus(
  invitationId: string,
  store: TokenStore,
): Promise<EmailDelivery | null> {
  const res = await apiFetch<{ delivery: EmailDelivery | null }>(
    `/invitations/${encodeURIComponent(invitationId)}/email-status`,
    {},
    store,
  );
  return res.delivery;
}


// ── Privacy notice (UK GDPR Art. 13) ─────────────────────────────────────────

export type PrivacyNoticeSection = { id: string; heading: string; body: string };

export type PrivacyNotice = {
  version: string;
  effectiveFrom: number;
  sections: PrivacyNoticeSection[];
  textSha256: string;
};

/**
 * PUBLIC - no token. Callable before sign-in and by people who have no account
 * at all, which is the point: Art. 13 information must reach a prospective user
 * before their data is obtained, and a contract signatory who never logs in.
 */
export async function getPrivacyNotice(): Promise<PrivacyNotice> {
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await classifiedFetch(`${base}/privacy`, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error('Could not load the privacy notice.');
  return (await res.json()) as PrivacyNotice;
}

/**
 * Record that the current user was SHOWN the notice. Not an acceptance - there
 * is no consent to give, because the lawful bases are contract and legitimate
 * interests. This is the evidence that we provided it (Art. 5(2)).
 */
export async function acknowledgePrivacyNotice(
  version: string,
  store: TokenStore,
): Promise<{ recorded: boolean; version: string }> {
  return apiFetch<{ recorded: boolean; version: string }>(
    '/me/privacy-notice/acknowledge',
    { method: 'POST', body: JSON.stringify({ version }) },
    store,
  );
}

export async function dismissSelfCertPrompt(store: TokenStore): Promise<{ recorded: boolean }> {
  return apiFetch<{ recorded: boolean }>(
    '/me/self-cert-prompt/dismiss',
    { method: 'POST' },
    store,
  );
}

export async function dismissAutoMasteryNotice(store: TokenStore): Promise<{ recorded: boolean }> {
  return apiFetch<{ recorded: boolean }>('/me/auto-mastery-notice/dismiss', { method: 'POST' }, store);
}

/** Dismiss the first-login walkthrough for one practice role (Practice Compass). */
export async function dismissRoleIntro(
  roleKey: string,
  store: TokenStore,
): Promise<{ recorded: boolean }> {
  return apiFetch<{ recorded: boolean }>(
    `/me/role-intro/${encodeURIComponent(roleKey)}/dismiss`,
    { method: 'POST' },
    store,
  );
}

/** One outstanding session on the personal desk. Server: GET /me/work. */
export interface MyWorkSession {
  encounterId: string;
  caseId: string;
  practiceId: string;
  practiceName: string;
  recipientName: string;
  encounterType: string;
  startedAt: number;
  endedAt: number | null;
}

export interface MyUnfinishedSession extends MyWorkSession {
  /** Saving now is a late save, without notes. */
  savesLate: boolean;
  editWindowClosesAt: number;
}

export interface MyWork {
  unfinishedSessions: MyUnfinishedSession[];
}

export async function getMyWork(store: TokenStore): Promise<MyWork> {
  return apiFetch<MyWork>('/me/work', {}, store);
}


export interface MyDbsDeclaration {
  certificateNumber: string;
  issuedOn: number | null;
  onUpdateService: boolean;
  attestationText: string;
  attestedAt: number;
  current: boolean;
}

export async function getMyDbsDeclaration(
  store: TokenStore,
): Promise<{ declaration: MyDbsDeclaration | null; statement: string; promptDismissed: boolean }> {
  return await apiFetch<{ declaration: MyDbsDeclaration | null; statement: string; promptDismissed: boolean }>(
    '/me/dbs-declaration',
    { method: 'GET' },
    store,
  );
}

export async function putMyDbsDeclaration(
  body: { certificateNumber: string; issuedOn?: number | null; onUpdateService?: boolean },
  store: TokenStore,
): Promise<void> {
  await apiFetch<{ recorded: boolean }>(
    '/me/dbs-declaration',
    { method: 'PUT', body: JSON.stringify(body) },
    store,
  );
}

export async function deleteMyDbsDeclaration(store: TokenStore): Promise<void> {
  await apiFetch<{ withdrawn: boolean }>('/me/dbs-declaration', { method: 'DELETE' }, store);
}

export async function dismissDbsPrompt(store: TokenStore): Promise<void> {
  await apiFetch<{ recorded: boolean }>('/me/dbs-prompt/dismiss', { method: 'POST' }, store);
}
